import { useCallback, useState } from 'react';

import type { UseFormSetError, UseFormSetValue } from 'react-hook-form';
import { useMutation } from 'react-query';

import type { UpdateAddressDataInput } from 'api/addressData';
import addressData from 'api/addressData';
import type { CreatePatientsInput } from 'api/patient/patients';
import patients from 'api/patient/patients';
import type { UpdatePersonalDataInput } from 'api/personalData';
import personalData from 'api/personalData';
import type { PhoneUpdateInput } from 'api/users';
import users from 'api/users';
import { useAuthContext } from 'components/context/AuthContext/AuthContext';
import type { PatientFormInput } from 'components/UI/organisms/PatientPersonalInfo/_constants/patientFormKeys';
import { PATIENT_FORM_INPUT_KEYS } from 'components/UI/organisms/PatientPersonalInfo/_constants/patientFormKeys';
import useOnBackendFormError from 'hooks/useOnBackendFormError/useOnBackendFormError';
import { useShowSnackbar } from 'hooks/useShowSnackbar/useShowSnackbar';
import snackbarMessages from 'translations/common/snackbar.mjs';

export const CREATE_PATIENT_MUTATION_KEY = 'Patient create';
export const UPDATE_PATIENT_NUMBER_MUTATION_KEY = 'Patient number update';
export const REVERT_PATIENT_NUMBER_MUTATION_KEY = 'Patient number revert';

type UsePatientMutationArguments = {
  setError: UseFormSetError<PatientFormInput>;
  setValue: UseFormSetValue<PatientFormInput>;
  onLoadingEnd: () => void;
  refreshData: (config: { action: 'create' | 'update' }) => void;
  isPro: boolean;
  patientId: number | null;
  personalDataId: number | null;
  patientAddressId: number | null;
};

const usePatientMutations = ({
  setError,
  setValue,
  onLoadingEnd,
  refreshData,
  isPro,
  patientId,
  personalDataId,
  patientAddressId,
}: UsePatientMutationArguments) => {
  const [generalErrors, setGeneralErrors] = useState<string[]>();
  const { userInfo } = useAuthContext();
  // const { patient } = usePatientContext();
  const { showSnackbar } = useShowSnackbar();

  const setGeneralErrorsWithLoadingEnd = (errors: string[]) => {
    onLoadingEnd();
    setGeneralErrors(errors);
  };
  const showSuccessSnackbar = useCallback(() => {
    showSnackbar({ variant: 'success', translationArray: snackbarMessages.success });
  }, []);

  const onErrorCreate = useOnBackendFormError<PatientFormInput, CreatePatientsInput>({
    setError,
    setGeneralErrors: setGeneralErrorsWithLoadingEnd,
  });

  const createPersonalDataMutation = useMutation(CREATE_PATIENT_MUTATION_KEY, patients.createPatient(), {
    onSuccess: () => {
      refreshData({ action: 'create' });
      showSuccessSnackbar();
    },
    onError: onErrorCreate,
    retry: false,
  });

  const onErrorUpdate = useOnBackendFormError<PatientFormInput, UpdatePersonalDataInput>({
    setError,
    setGeneralErrors: setGeneralErrorsWithLoadingEnd,
  });

  // update is only available when personalDataId is present
  const updatePersonalDataMutation = useMutation(personalData.patchPersonalData(personalDataId as number, isPro), {
    onSuccess: showSuccessSnackbar,
    onError: onErrorUpdate,
  });

  const onErrorUpdateAddress = useOnBackendFormError<PatientFormInput, UpdateAddressDataInput>({
    setError,
    setGeneralErrors: setGeneralErrorsWithLoadingEnd,
  });

  // update is only available when patientAddressId is present
  const updateAddressDataMutation = useMutation(addressData.patchAddressData(patientAddressId as number, isPro), {
    // omitted onSuccess: showSuccessSnackbar to avoid doubled success snackbar with updatePersonalDataMutation
    onError: onErrorUpdateAddress,
  });

  const onErrorNumberUpdate = useOnBackendFormError<PatientFormInput, PhoneUpdateInput>({
    setError,
    setGeneralErrors: setGeneralErrorsWithLoadingEnd,
  });

  const updateNumberMutation = useMutation(UPDATE_PATIENT_NUMBER_MUTATION_KEY, users.phoneUpdate(), {
    onError: onErrorNumberUpdate,
  });

  const onErrorNumberRevert = useOnBackendFormError<PatientFormInput, undefined>({
    setError,
    setGeneralErrors: setGeneralErrorsWithLoadingEnd,
  });

  const revertNumberMutation = useMutation(REVERT_PATIENT_NUMBER_MUTATION_KEY, users.phoneUpdateRevert(), {
    onSuccess: () => {
      if (userInfo[PATIENT_FORM_INPUT_KEYS.phoneNumber as 'phoneNumber'])
        setValue(PATIENT_FORM_INPUT_KEYS.phoneNumber, userInfo[PATIENT_FORM_INPUT_KEYS.phoneNumber as 'phoneNumber'] as string);
      // omitted showSuccessSnackbar to avoid doubled success snackbar with updatePersonalDataMutation
    },
    onError: onErrorNumberRevert,
  });

  const partialUpdatePatientBaseData = useMutation(patients.partialUpdatePatientBaseData(patientId as number, isPro));

  return {
    createPersonalDataMutation,
    updatePersonalDataMutation,
    updateAddressDataMutation,
    updateNumberMutation,
    revertNumberMutation,
    partialUpdatePatientBaseData,
    patientMutationsGeneralError: generalErrors,
  };
};

export default usePatientMutations;
